import { useEffect, useState } from "react";
import axios from "axios";
import { useI18n } from "@/i18n/I18nProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Small pill showing real waitlist count.
 * Only renders once we've fetched — no fake or placeholder numbers.
 * Silently hides on error (never shows "0" during loading).
 */
export default function LiveCounter({
    endpoint = "/api/waitlist/stats",
    labelSingular,
    labelPlural,
    variant = "light",
    testid = "live-counter",
}) {
    const { t } = useI18n();
    const [total, setTotal] = useState(null);
    const singular = labelSingular ?? t("lc.one");
    const plural = labelPlural ?? t("lc.many");

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const { data } = await axios.get(`${BACKEND_URL}${endpoint}`);
                if (!alive) return;
                const value = typeof data?.total === "number" ? data.total : null;
                setTotal(value);
            } catch {
                if (!alive) return;
                setTotal(null);
            }
        };
        load();
        const t = setInterval(load, 60000); // refresh every 60s
        return () => {
            alive = false;
            clearInterval(t);
        };
    }, [endpoint]);

    if (total === null || total < 1) return null;

    const label = total === 1 ? singular : plural;
    const isDark = variant === "dark";

    return (
        <div
            data-testid={testid}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 14px 8px 10px",
                borderRadius: 999,
                background: isDark ? "rgba(247,244,238,0.08)" : "rgba(15,61,62,0.06)",
                border: isDark ? "1px solid rgba(247,244,238,0.18)" : "1px solid rgba(15,61,62,0.14)",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                fontWeight: 500,
                color: isDark ? "var(--itly-on-teal)" : "var(--itly-fg-strong)",
                letterSpacing: "0.005em",
            }}
        >
            <PulseDot />
            <span>
                <strong style={{ fontWeight: 700 }}>{total}</strong> {label}
            </span>
        </div>
    );
}

function PulseDot() {
    return (
        <span
            aria-hidden="true"
            style={{
                position: "relative",
                width: 8,
                height: 8,
                borderRadius: 999,
                background: "var(--itly-orange-500)",
                display: "inline-block",
                flexShrink: 0,
            }}
        />
    );
}
